import { User } from 'firebase/auth';

const API_URL = process.env.REACT_APP_API_URL;

export type ExternalSource = {
  id: string;
  name: string;
  description: string;
};

export type ExternalImportRunStatus =
  | 'running'
  | 'cancel_requested'
  | 'cancelled'
  | 'completed'
  | 'failed';

export type ExternalImportRun = {
  id: string;
  sourceId: string;
  status: ExternalImportRunStatus;
  startedAt: string;
  updatedAt?: string;
  completedAt?: string;
  startedBy?: string;
  dryRun: boolean;
  total: number;
  nextIndex: number;
  processed: number;
  queued: number;
  skippedExisting: number;
  skippedRejected: number;
  skippedDuplicate: number;
  skippedCategory: number;
  skippedNotAMeal: number;
  skippedIncomplete: number;
  skippedLowConfidence: number;
  failed: number;
  failures: { externalId: string; detail: string }[];
};

const getHeaders = async (user: User) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${await user.getIdToken()}`,
});

export const fetchExternalSources = async (
  user: User
): Promise<ExternalSource[]> => {
  const response = await fetch(
    `${API_URL}/api/admin/external-sources`,
    { headers: await getHeaders(user) }
  );

  if (!response.ok) throw new Error('Failed to fetch external sources');
  const data = await response.json();
  return data.sources;
};

export const startExternalImport = async (
  user: User,
  options: {
    source: string;
    mode: 'sample' | 'full';
    sampleSize?: number;
    imageStrategy?: 'source' | 'generate' | 'none';
    /** Normalized course scope; omitted = the dinner default (mains + soups). */
    categories?: string[] | 'all';
    includeRejected?: boolean;
  }
): Promise<{ runId: string; total: number }> => {
  const response = await fetch(
    `${API_URL}/api/admin/external-import`,
    {
      method: 'POST',
      headers: await getHeaders(user),
      body: JSON.stringify(options),
    }
  );

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to start import');
  }
  return response.json();
};

export const fetchExternalImportRuns = async (
  user: User,
  limit = 10
): Promise<ExternalImportRun[]> => {
  const response = await fetch(
    `${API_URL}/api/admin/external-import-runs?limit=${limit}`,
    { headers: await getHeaders(user) }
  );

  if (!response.ok) throw new Error('Failed to fetch import runs');
  const data = await response.json();
  return data.runs;
};

export const cancelExternalImportRun = async (
  user: User,
  runId: string
): Promise<void> => {
  const response = await fetch(
    `${API_URL}/api/admin/external-import-runs/${runId}/cancel`,
    { method: 'POST', headers: await getHeaders(user) }
  );

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to cancel import run');
  }
};
